<template>
    <!-- AI Assistant Button - Shows when AI could be helpful -->
    <Transition name="ai-button" appear>
        <div v-if="showAiButton" class="mt-5 overflow-hidden">
            <button
                @click="handleAskAssistant"
                class="ai-bg-gradient flex font-medium items-center gap-2 leading-none px-3 py-2 rounded-md text-xs text-v2-white"
            >
                <BaseSvg name="Flare" aria-hidden="true" class="h-[14px]" />
                Search resources
            </button>
        </div>
    </Transition>

    <!-- AI Thinking State - Shows while AI is processing -->
    <div v-if="isThinking" class="ai-border-thinking mt-6 p-4">
        <span class="gap-2 inline-flex items-center">
            <BaseSvg name="FlareGrad" aria-hidden="true" />
            <span class="animate-pulse font-medium text-sm">Thinking…</span>
        </span>
    </div>

    <!-- AI Results Loading Skeleton - Shows briefly when results are being prepared -->
    <div v-if="isLoadingResults" class="ai-border-ready mt-6 p-10">
        <div class="loading-value h-7 w-64 mb-8 rounded"></div>
        <ol class="space-y-8">
            <li v-for="n in 2" :key="n">
                <div class="flex gap-3">
                    <!-- Icon Skeleton -->
                    <div class="aspect-square flex h-20 items-center justify-center rounded-lg bg-gray-200 dark:bg-v2-gray-50">
                        <div class="loading-value h-8 w-8 rounded"></div>
                    </div>
                    <!-- Content Skeleton -->
                    <div class="flex-1">
                        <div class="flex gap-3 items-center mb-1">
                            <div class="loading-value h-5 w-48 rounded"></div>
                            <div class="loading-value h-4 w-16 rounded"></div>
                        </div>
                        <div class="loading-value h-3 w-full mb-2 rounded"></div>
                        <div class="loading-value h-3 w-3/4 mb-3 rounded"></div>
                        <div class="flex gap-1 items-center">
                            <div class="loading-value h-5 w-5 rounded"></div>
                            <div class="loading-value h-3 w-56 rounded"></div>
                        </div>
                    </div>
                </div>
            </li>
        </ol>
    </div>

    <!-- AI Results - Shows when AI has completed processing -->
    <div v-if="hasResults" class="ai-border-ready mt-6 p-10">
        <h3 class="font-semibold mb-8 text-lg">{{ userQuestion }}</h3>
        <ol class="space-y-8">
            <li v-for="result in aiResults" :key="result.id">
                <a href="#" class="flex gap-3 group">
                    <!-- Icon -->
                    <div :class="[
                        'aspect-square flex h-20 items-center justify-center rounded-lg dark:bg-v2-gray-50',
                        result.type === 'document' ? 'bg-green bg-opacity-10' : 'bg-blue-650 bg-opacity-10'
                    ]">
                        <BaseSvg
                            :name="result.type === 'document' ? 'Document' : 'PlayCircle'"
                            aria-hidden="true"
                            class="dark:fill-v2-gray-90"
                        />
                    </div>
                    <!-- Content -->
                    <div>
                        <div class="flex gap-3 items-center">
                            <h4 class="font-medium group-hover:underline">{{ result.title }}</h4>
                            <span class="bg-v2-gray-130 leading-none px-1.5 py-1 rounded-md text-[12px] dark:bg-v2-gray-20 dark:text-v2-gray-80">
                                {{ result.category }}
                            </span>
                        </div>
                        <p class="mt-1 text-v2-gray-80 text-xs dark:text-v2-gray-90">
                            <q>{{ result.description }}</q>
                        </p>
                        <p class="flex gap-1 items-center mt-2.5 text-xs dark:text-v2-gray-80">
                            <BaseSvg name="FlareGrad" aria-hidden="true" class="h-5 w-auto" />
                            {{ result.aiInsight }}
                        </p>
                    </div>
                </a>
            </li>
        </ol>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { BaseSvg } from "@/Components";

const props = defineProps({
    searchTerm: {
        type: String,
        default: ''
    },
    // Controls when the AI button should be visible
    // In real implementation, this would be determined by analyzing the search term
    shouldShowAiButton: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['ai-started', 'ai-completed']);

// AI State Management
const aiState = ref('idle'); // 'idle', 'thinking', 'loading-results', 'completed'
const userQuestion = ref('');
const aiResults = ref([]);

// Computed properties for template
const showAiButton = computed(() => {
    return props.shouldShowAiButton && aiState.value === 'idle' && props.searchTerm.length > 0;
});

const isThinking = computed(() => aiState.value === 'thinking');
const isLoadingResults = computed(() => aiState.value === 'loading-results');
const hasResults = computed(() => aiState.value === 'completed' && aiResults.value.length > 0);

// AI Flow Implementation
const handleAskAssistant = async () => {
    // Store the user's question
    userQuestion.value = props.searchTerm;

    // Start thinking state
    aiState.value = 'thinking';
    emit('ai-started');

    // Simulate AI processing (replace with real API call)
    await simulateAiProcessing();

    // Complete with dummy results
    aiState.value = 'completed';
    emit('ai-completed', aiResults.value);
};

// Dummy AI processing simulation
const simulateAiProcessing = async () => {
    // Simulate 2 seconds of thinking time
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Switch to loading results state
    aiState.value = 'loading-results';

    // Simulate 800ms of results preparation
    await new Promise(resolve => setTimeout(resolve, 800));

    // Generate dummy results based on search term
    aiResults.value = generateDummyResults(props.searchTerm);
};

// Generate dummy AI results for demonstration
const generateDummyResults = (searchTerm) => {
    // This is dummy data - in real implementation, this would come from your AI service
    const dummyResults = [
        {
            id: 1,
            type: 'document',
            title: `${searchTerm} Installation Instructions`,
            category: 'Instructions',
            description: 'Step-by-step instructions for the setup, spring tension, and troubleshooting. See page 4 for common issues.',
            aiInsight: 'This PDF gives detailed adjustment guidance, especially for fine-tuning arm tension and ensuring ADA compliance.'
        },
        {
            id: 2,
            type: 'video',
            title: `${searchTerm} Adjustment Video`,
            category: 'Video',
            description: '2-minute walkthrough using a hex key — great for quick field reference.',
            aiInsight: 'A short visual guide to help you get the arm angle and tension right without overcorrecting.'
        }
    ];

    return dummyResults;
};

// Reset AI state when search term changes
watch(() => props.searchTerm, () => {
    if (aiState.value !== 'idle') {
        aiState.value = 'idle';
        userQuestion.value = '';
        aiResults.value = [];
    }
});

// Expose methods for parent component
defineExpose({
    resetAiState: () => {
        aiState.value = 'idle';
        userQuestion.value = '';
        aiResults.value = [];
    },
    isAiActive: computed(() => aiState.value !== 'idle')
});
</script>

<style scoped>
/* AI Button Expand Animation */
.ai-button-enter-active,
.ai-button-leave-active {
    transition: all 0.4s cubic-bezier(0.4, 0.0, 0.2, 1);
    transform-origin: top;
}

.ai-button-enter-from {
    opacity: 0;
    transform: translateY(-10px) scaleY(0.8);
    max-height: 0;
}

.ai-button-enter-to {
    opacity: 1;
    transform: translateY(0) scaleY(1);
    max-height: 100px; /* Sufficient for button height */
}

.ai-button-leave-from {
    opacity: 1;
    transform: translateY(0) scaleY(1);
    max-height: 100px;
}

.ai-button-leave-to {
    opacity: 0;
    transform: translateY(-10px) scaleY(0.8);
    max-height: 0;
}

</style>
